import { BroadcastChannel } from 'broadcast-channel';
import { io, Socket } from 'socket.io-client';

import { CommunicationMessageTypes } from './constants';
import createLogger, { LoggerColumns } from './logger';
import { is } from './type-utils';
import { getElectronConfig, stripProtocol } from './utils';

const logger = createLogger('communication-adapter.ts', {
  columns: [LoggerColumns.COMMUNICATION_ADAPTER],
  tags: ['CMA'],
});

enum CommunicationProtocol {
  SOCKET = 'SOCKET',
  BROADCAST = 'BROADCAST',
}
type BroadCastProtocol = {
  protocol: CommunicationProtocol.BROADCAST;
};
type SocketProtocol = {
  protocol: CommunicationProtocol.SOCKET;
  serverIp: string;
  serverPort: string;
};
// Can have a better name
type MessageContract = {
  id: number;
  messageType: CommunicationMessageTypes;
};
const isSocketProtocol = is<BroadCastProtocol | SocketProtocol, SocketProtocol>(
  (arg) => (arg.protocol === CommunicationProtocol.SOCKET ? arg : undefined)
);
const isBroadcastProtocol = is<
  BroadCastProtocol | SocketProtocol,
  BroadCastProtocol
>((arg) =>
  arg.protocol === CommunicationProtocol.BROADCAST ? arg : undefined
);

class CommunicationAdapterClass {
  protocol: CommunicationProtocol = CommunicationProtocol.BROADCAST;
  serverIp!: string;
  serverPort!: string;
  isReady: boolean = false;
  socket!: Socket;
  listenerId: number = 0;
  broadcastChannels: {
    [k: number]: BroadcastChannel;
  } = {};
  _listeners: {
    [k: string]: {
      [n: number]: (message: any) => void;
    };
  } = {};
  constructor(args: BroadCastProtocol | SocketProtocol) {
    this.protocol = args.protocol;
    if (isSocketProtocol(args)) {
      this.serverIp = args.serverIp;
      this.serverPort = args.serverPort;

      // Connect to socket and wait for ready
      this.socket = io(`http://${this.serverIp}:${this.serverPort}`, {
        reconnectionAttempts: 10,
      });
      this.socket.on('connect', () => {
        this.isReady = true;
        logger.log(`Socket connection is ready`);
        this.startListeningToSocket();
      });
    } else if (isBroadcastProtocol(args)) {
      this.protocol = args.protocol;
      this.isReady = true;
    } else {
      // This should never happen, ideally
      console.error('Unknown communication protocol');
      alert(`Unknown communication protocol${this.protocol}`);
    }
  }
  checkSocketValidity(): boolean {
    if (!this.isSocket || !this.isReady || !this.socket) {
      throw new Error('Invalid communication adapter state CMA76');
    }
    return true;
  }

  private startListeningToSocket() {
    if (this.checkSocketValidity()) {
      this.socket.on('message', (communication) => {
        const { room, commPayload } = communication;
        if (this._listeners[room]) {
          Object.values(this._listeners[room]).forEach((listener) => {
            listener.call(undefined, commPayload);
          });
        }
      });
    }
  }
  get isSocket() {
    return this.protocol === CommunicationProtocol.SOCKET;
  }
  get isBroadCast() {
    return this.protocol === CommunicationProtocol.BROADCAST;
  }

  _postToSocket<T>(messageType: CommunicationMessageTypes, commPayload: T) {
    if (this.isSocket) {
      this.socket.emit('emit-to-room', {
        room: messageType,
        commPayload,
      });
      return;
    }
    console.error('Invalid state in communication adapter CMA119');
  }

  _receiveMessage(
    messageType: CommunicationMessageTypes,
    fn: (message: any) => void
  ): MessageContract {
    this.listenerId++;
    this._listeners[messageType] = this._listeners[messageType] ?? {};
    this._listeners[messageType][this.listenerId] = fn;
    return {
      id: this.listenerId,
      messageType,
    };
  }
  removeHandler(contract: MessageContract): void {
    if (this._listeners[contract.messageType]) {
      delete this._listeners[contract.messageType][contract.id];
    }
  }
}

const initObj = ((): BroadCastProtocol | SocketProtocol => {
  const electronConfig = getElectronConfig();
  if (
    electronConfig?.communicationMode === 'socket' &&
    electronConfig?.socketServerIP &&
    electronConfig?.socketServerPort
  ) {
    const socketServerPort = electronConfig.socketServerPort;
    const socketServerIp = stripProtocol(electronConfig.socketServerIP);
    logger.columns([LoggerColumns.CONFIG]).log('get config from electron', {
      socketServerPort,
      socketServerIp,
    });
    return {
      protocol: CommunicationProtocol.SOCKET,
      serverIp: socketServerIp,
      serverPort: socketServerPort,
    };
  }
  return process.env.REACT_APP_COMM_PROTOCOL === 'socket'
    ? {
        protocol: CommunicationProtocol.SOCKET,
        serverIp: process.env.REACT_APP_SOCKET_IP || '127.0.0.1',
        serverPort: process.env.REACT_APP_SOCKET_PORT || '8090',
      }
    : {
        protocol: CommunicationProtocol.BROADCAST,
      };
})();
logger
  .columns([LoggerColumns.CONFIG])
  .log(`building communication ${initObj.protocol} `, {
    initObj,
    env: process.env,
  });

const ca = new CommunicationAdapterClass(initObj);

export default class Communicator<T> {
  private broadCastChannel!: BroadcastChannel;
  private contract!: MessageContract;
  constructor(public messageType: CommunicationMessageTypes) {
    if (ca.isSocket) {
      if (ca.checkSocketValidity()) {
        ca.socket.emit('join-room', { room: messageType });
      }
    } else {
      this.broadCastChannel = new BroadcastChannel<T>(messageType, {
        webWorkerSupport: false,
      });
    }
  }
  postMessage(message: T) {
    if (this.messageType !== CommunicationMessageTypes.LogControl) {
      logger.log(`sending ${this.messageType}`, message);
    }
    if (ca.isSocket) {
      ca._postToSocket(this.messageType, message);
    } else {
      this.broadCastChannel.postMessage(message);
    }
  }
  receiveMessage(fn: (message: T) => void): void {
    if (ca.isSocket) {
      this.contract = ca._receiveMessage(this.messageType, fn);
    }
    if (ca.isBroadCast) {
      this.broadCastChannel.onmessage = (...args) => {
        if (this.messageType !== CommunicationMessageTypes.LogControl) {
          logger.log(`receiving ${this.messageType}`, { args });
        }
        fn(...args);
      };
    }
  }
  close(): void {
    this.broadCastChannel?.close();
    if (this.contract) {
      ca.removeHandler(this.contract);
    }
  }
  static get isSocket() {
    return ca.isSocket;
  }
  static get isBroadCast() {
    return ca.isBroadCast;
  }
  static get isReady() {
    return ca.isReady;
  }
}
